import { Vector3 } from 'three';
import type { Room } from './Room';
import { ALL_SURFACE_IDS, type Surface } from './Surface';

export interface RayHit {
  distance: number;
  point: Vector3;
  normal: Vector3;
  kind: 'surface' | 'obstacle';
  surface?: Surface;
}

const scratchPoint = new Vector3();
const scratchNormal = new Vector3();
const scratchLocal = { u: 0, v: 0 };
const scratchBoxMin = new Vector3();
const scratchBoxMax = new Vector3();
const scratchClosest = new Vector3();
const scratchDelta = new Vector3();
const scratchPush = new Vector3();
const scratchEscapePoint = new Vector3();
const scratchEscape: EscapeAxis = { axis: 'x', to: 0 };
/** How far inside the room an escape has to land to be preferred. */
const ESCAPE_MARGIN = 1e-4;

/**
 * Analytic collision queries for the chamber: ray casts against the six
 * walkable planes and the obstacle boxes, plus sphere push-out for obstacles.
 * No physics engine is involved (see ADR-0003).
 */
export class CollisionWorld {
  constructor(readonly room: Room) {}

  /** Nearest hit along the ray, or null. */
  raycast(origin: Vector3, direction: Vector3, maxDistance: number): RayHit | null {
    let best: RayHit | null = null;

    for (const surface of this.room.surfaces.values()) {
      const denominator = direction.dot(surface.normal);
      if (Math.abs(denominator) < 1e-6) continue;
      const t =
        ((surface.origin.x - origin.x) * surface.normal.x +
          (surface.origin.y - origin.y) * surface.normal.y +
          (surface.origin.z - origin.z) * surface.normal.z) /
        denominator;
      if (t <= 1e-4 || t >= maxDistance) continue;

      scratchPoint.copy(origin).addScaledVector(direction, t);
      surface.toLocal(scratchPoint, scratchLocal);
      if (!surface.contains(scratchLocal.u, scratchLocal.v)) continue;
      // Doorways are holes in the wall: rays pass straight through.
      if (surface.containsHole(scratchLocal.u, scratchLocal.v)) continue;

      if (!best || t < best.distance) {
        best = {
          distance: t,
          point: scratchPoint.clone(),
          normal: surface.normal.clone(),
          kind: 'surface',
          surface,
        };
      }
    }

    for (const obstacle of this.room.obstacles) {
      const hit = rayBox(origin, direction, obstacle.center, obstacle.halfSize, maxDistance);
      if (!hit) continue;
      if (!best || hit.distance < best.distance) {
        best = {
          distance: hit.distance,
          point: hit.point,
          normal: hit.normal,
          kind: 'obstacle',
        };
      }
    }

    return best;
  }

  /**
   * Push a sphere out of any obstacle it overlaps.
   * Returns the number of contacts that were resolved.
   *
   * `normal` keeps the correction in that plane: a body walking on a surface is
   * pushed sideways out of a crate, never through its own floor.
   *
   * An escape that would leave the room is only used when no roomier one
   * exists. A body squeezed between a crate and a wall would otherwise be
   * pushed out of the crate and back in by the wall, every frame, forever.
   */
  resolveObstacles(position: Vector3, radius: number, normal?: Vector3): number {
    let contacts = 0;

    for (const obstacle of this.room.obstacles) {
      scratchBoxMin.copy(obstacle.center).sub(obstacle.halfSize);
      scratchBoxMax.copy(obstacle.center).add(obstacle.halfSize);
      const min = scratchBoxMin;
      const max = scratchBoxMax;

      scratchClosest.set(
        Math.min(Math.max(position.x, min.x), max.x),
        Math.min(Math.max(position.y, min.y), max.y),
        Math.min(Math.max(position.z, min.z), max.z),
      );

      scratchDelta.copy(position).sub(scratchClosest);
      const distance = scratchDelta.length();

      if (distance > radius) continue;
      contacts += 1;

      if (distance > 1e-5) {
        scratchPush.copy(scratchDelta).divideScalar(distance);
        if (normal) {
          scratchPush.addScaledVector(normal, -scratchPush.dot(normal));
          if (scratchPush.lengthSq() > 1e-6) scratchPush.normalize();
        }
        if (scratchPush.lengthSq() > 1e-6) {
          scratchEscapePoint.copy(position).addScaledVector(scratchPush, radius - distance);
          if (this.insideRoom(scratchEscapePoint, ESCAPE_MARGIN)) {
            position.copy(scratchEscapePoint);
            continue;
          }
        }
      }

      // The centre is inside the box, or the straight push would leave the
      // room: escape through the nearest face that stays inside it.
      this.chooseEscape(position, min, max, radius, normal, scratchEscape);
      position[scratchEscape.axis] = scratchEscape.to;
    }

    return contacts;
  }

  /** True when a point sits inside the room, `margin` clear of every face. */
  isInsideRoom(point: Vector3, margin = 0): boolean {
    return this.insideRoom(point, margin);
  }

  /** True when two points share a zone; a shot must stay in the body's zone. */
  sameZone(a: Vector3, b: Vector3): boolean {
    const zone = this.room.zoneAt(a);
    return zone !== null && zone === this.room.zoneAt(b);
  }

  /** True when a point sits inside the room, `margin` clear of every face. */
  private insideRoom(point: Vector3, margin: number): boolean {
    const zoneId = this.room.zoneAt(point);
    if (!zoneId) return false;
    for (const face of ALL_SURFACE_IDS) {
      const surface = this.room.getSurface(this.room.keyFor(zoneId, face));
      if (!surface) continue;
      if (surface.signedDistance(point) < margin) return false;
    }
    return true;
  }

  /**
   * Fill `out` with the shortest escape from a box, preferring one that stays
   * inside the room. Falls back to the shortest escape of all when every side
   * is walled in, so the caller always gets a direction back.
   */
  private chooseEscape(
    position: Vector3,
    min: Vector3,
    max: Vector3,
    radius: number,
    normal: Vector3 | undefined,
    out: EscapeAxis,
  ): EscapeAxis {
    const pick = (roomyOnly: boolean): boolean => {
      let found = false;
      let bestDistance = Infinity;
      for (const axis of escapeAxes(normal)) {
        for (const face of [0, 1] as const) {
          const to = face === 0 ? min[axis] - radius : max[axis] + radius;
          const distance = Math.abs(position[axis] - to);
          if (distance >= bestDistance) continue;
          if (roomyOnly) {
            scratchEscapePoint.copy(position);
            scratchEscapePoint[axis] = to;
            if (!this.insideRoom(scratchEscapePoint, ESCAPE_MARGIN)) continue;
          }
          bestDistance = distance;
          out.axis = axis;
          out.to = to;
          found = true;
        }
      }
      return found;
    };
    if (!pick(true)) pick(false);
    return out;
  }
}

/** Axes an escape may move along: the plane's, or all three without a plane. */
function escapeAxes(normal: Vector3 | undefined): Array<'x' | 'y' | 'z'> {
  const axes: Array<'x' | 'y' | 'z'> = ['x', 'y', 'z'];
  if (!normal) return axes;
  return axes.filter((axis) => Math.abs(normal[axis]) < 0.9);
}

interface EscapeAxis {
  axis: 'x' | 'y' | 'z';
  to: number;
}

interface BoxHit {
  distance: number;
  point: Vector3;
  normal: Vector3;
}

function rayBox(
  origin: Vector3,
  direction: Vector3,
  center: Vector3,
  halfSize: Vector3,
  maxDistance: number,
): BoxHit | null {
  const min = center.clone().sub(halfSize);
  const max = center.clone().add(halfSize);

  let tMin = 0;
  let tMax = maxDistance;
  let hitAxis: 'x' | 'y' | 'z' = 'x';
  let hitSign = -1;

  const axes: Array<'x' | 'y' | 'z'> = ['x', 'y', 'z'];
  for (const axis of axes) {
    const d = direction[axis];
    const o = origin[axis];
    if (Math.abs(d) < 1e-8) {
      if (o < min[axis] || o > max[axis]) return null;
      continue;
    }
    let t1 = (min[axis] - o) / d;
    let t2 = (max[axis] - o) / d;
    let sign = -1;
    if (t1 > t2) {
      const swap = t1;
      t1 = t2;
      t2 = swap;
      sign = 1;
    }
    if (t1 > tMin) {
      tMin = t1;
      hitAxis = axis;
      hitSign = sign;
    }
    if (t2 < tMax) tMax = t2;
    if (tMin > tMax) return null;
  }

  const distance = tMin >= 1e-4 ? tMin : tMax;
  if (distance < 1e-4 || distance > maxDistance) return null;

  scratchNormal.set(0, 0, 0);
  scratchNormal[hitAxis] = hitSign;
  scratchPoint.copy(origin).addScaledVector(direction, distance);

  return { distance, point: scratchPoint.clone(), normal: scratchNormal.clone() };
}
